import { useState, useEffect, useMemo, useCallback } from "react";
import { useRouter } from "expo-router";
import { useWindowDimensions } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { Button } from "@src/components/Button";
import { FullScreenLoading, EmptyState } from "@src/components/screen-state";
import { ClipVideoPlayer } from "@src/features/clips";
import type { Clip } from "@src/features/clips";
import { useSupportUserStatus } from "@src/features/tenants";
import { SupportAccessRestricted } from "@src/components/web/SupportAccessRestricted.web";
import { FEATURE_FLAGS, useBooleanFlag } from "@src/services/feature-flags/feature-flags";
import { showCustomToast } from "@src/components/CustomToast/utils/show-custom-toast";
import { useClips, type ClipItem } from "@visioni/a3s-media/app";
import { toISODateString } from "@visioni/a3s-codecs";
import { SiteId, AssetCameraKey } from "@visioni/a3s-schema";
import {
  createDumpingSearchState,
  getMidpoint,
  markWastePresent,
  markNoWaste,
  markSkip,
  undo,
} from "@visioni/a3s-dumping";
import { ConfirmDumpModal, type ConfirmDumpFormValue } from "./ConfirmDumpModal.web";
import { useConfirmDump } from "@visioni/a3s-dumping/app";
import { useRegenerateWasteSenseForWeek } from "@src/features/wastesense/hooks/useRegenerateWasteSenseForWeek";

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString("en-AU", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

/**
 * Adapt a media-service {@link ClipItem} to the clips feature's {@link Clip},
 * so the dumping search can reuse {@link ClipVideoPlayer}. `video.url` is the
 * canonical full-resolution source; `videoUrl` is the deprecated fallback kept
 * for older deployed apps. The clip's start time doubles as its id/timestamp.
 */
function clipItemToClip(item: ClipItem): Clip {
  return {
    id: item.startTime,
    timestamp: item.startTime,
    thumbnailUrl: item.thumbnailUrl ?? null,
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    videoUrl: item.video?.url ?? item.videoUrl ?? "",
    duration: item.durationMs,
  };
}

interface DumpingSearchContentProps {
  tenantId: string;
  cameraKey: string;
  siteId: string;
  /** ISO timestamp of the "no waste" bound, inherited from scene compare. */
  startTime: string;
  /** ISO timestamp of the "waste present" bound, inherited from scene compare. */
  endTime: string;
}

export function DumpingSearchContent({ tenantId, cameraKey, siteId, startTime, endTime }: DumpingSearchContentProps) {
  const router = useRouter();
  const supportStatus = useSupportUserStatus();
  const isSupportUser = supportStatus === "granted";
  // Reactive flag read: `useBooleanFlag` subscribes to this specific flag's
  // value, so the component re-renders (and re-evaluates the guard) when flags
  // hydrate. The previous `getBooleanFlag(...)` was a plain function call whose
  // result the React Compiler memoised on the stable getter identity — on a
  // cold deep-link the flags load just after auth settles, so `isEnabled` froze
  // `false` and the page showed "Access restricted" forever. See guard below.
  const isEnabled = useBooleanFlag(FEATURE_FLAGS.DUMPING_SEARCH);

  const parsedSiteId = useMemo(() => SiteId.fromString(siteId), [siteId]);
  const parsedCameraKey = useMemo(() => AssetCameraKey.fromString(cameraKey), [cameraKey]);
  const normalizedStartTime = useMemo(() => toISODateString(new Date(startTime)), [startTime]);
  const normalizedEndTime = useMemo(() => toISODateString(new Date(endTime)), [endTime]);

  const { data: clips, isFetchedAfterMount } = useClips({
    siteId: parsedSiteId,
    assetCameraKey: parsedCameraKey,
    startTime: normalizedStartTime,
    endTime: normalizedEndTime,
    enabled: isSupportUser && isEnabled,
  });

  // Clips ordered oldest-first so index 0 is the "no waste" end and the last
  // index is the "waste present" end — the bisect window runs from earliest
  // to latest, matching the good/bad bounds the user came in with.
  const orderedClips = useMemo(() => {
    if (!clips) return [];
    return [...clips].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
  }, [clips]);

  const handleExit = useCallback(() => router.back(), [router]);

  // While auth is still settling (cold deep-link / full reload), roles aren't
  // known yet — wait rather than deny. The feature-flag read below is now
  // reactive (useBooleanFlag), so the guard re-evaluates when flags hydrate;
  // the app shell (_layout.web.tsx) already awaits feature-flag init before
  // rendering routes, so flags are loaded by the time we get here.
  if (supportStatus === "pending") {
    return <FullScreenLoading message="Checking access..." />;
  }

  if (!isSupportUser || !isEnabled) {
    return <SupportAccessRestricted />;
  }

  // Gate on isFetchedAfterMount, not isLoading: useClips sets
  // refetchOnMount: "always", so with a warm cache it returns stale clips at
  // isLoading=false while a forced refetch is still in flight. Bisect freezes
  // its clip list on first mount, so mounting against that cache would bake in
  // an outdated/incomplete window. Waiting for the post-mount fetch (which also
  // re-arms when the bounds change the query key) guarantees a fresh snapshot.
  if (!isFetchedAfterMount) {
    return <FullScreenLoading message="Loading clips..." />;
  }

  if (orderedClips.length < 2) {
    return (
      <EmptyState
        icon="videoOutline"
        title="Not enough clips"
        message="There aren't enough clips between these snapshots to search."
      />
    );
  }

  // A dumping search bisects a fixed, ordered list of clips. Keying <Bisect> on
  // the bounds makes React remount it — with a fresh snapshot and fresh state —
  // whenever the user opens a new window, while a same-bounds background refetch
  // re-renders this loader but leaves the mounted Bisect (and its frozen clips)
  // untouched. That keeps the bisect from shifting indices under an in-progress
  // search or wiping a completed result, without any reset bookkeeping here.
  return (
    <Bisect
      key={`${normalizedStartTime}|${normalizedEndTime}`}
      clips={orderedClips}
      onExit={handleExit}
      tenantId={tenantId}
      siteId={siteId}
      cameraKey={cameraKey}
    />
  );
}

function Bisect({
  clips,
  onExit,
  tenantId,
  siteId,
  cameraKey,
}: {
  clips: ClipItem[];
  onExit: () => void;
  tenantId: string;
  siteId: string;
  cameraKey: string;
}) {
  const { theme } = useUnistyles();
  const { height: screenHeight, width: screenWidth } = useWindowDimensions();

  // Freeze the clips at mount: lazy initial state captures the snapshot once, so
  // later refetches that re-render the parent can't reach this list. A new window
  // remounts via the `key`, re-freezing against the fresh clips.
  const [searchClips] = useState(() => clips);
  const [state, setState] = useState(() => createDumpingSearchState(searchClips.length));

  // Measured height of the flex player area (between header and controls), used
  // to size the clip to fill the available space. See DumpingResult for detail.
  const [playerAreaHeight, setPlayerAreaHeight] = useState(0);

  const handleWastePresent = useCallback(() => setState(markWastePresent), []);
  const handleNoWaste = useCallback(() => setState(markNoWaste), []);
  const handleSkip = useCallback(() => setState(markSkip), []);
  const handleUndo = useCallback(() => setState(undo), []);

  const canUndo = state.history.length > 0;

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === "Escape") {
        onExit();
        return;
      }
      // Undo works from the search step and the result — Backspace reopens a
      // finished search by reverting the verdict that closed it. Prevent the
      // default Backspace-navigates-back behaviour so undo doesn't exit the
      // search on browsers that still map Backspace to history navigation.
      if (e.code === "Backspace" || e.code === "Delete") {
        e.preventDefault();
        handleUndo();
        return;
      }
      // While the result is showing, its own handler owns the arrow keys
      // (stepping through neighbouring clips), so don't double-handle here.
      if (state.done) return;
      if (e.code === "ArrowLeft") handleNoWaste();
      else if (e.code === "ArrowRight") handleWastePresent();
      else if (e.code === "KeyS") handleSkip();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onExit, state.done, handleNoWaste, handleWastePresent, handleSkip, handleUndo]);

  const timelineColors = {
    safe: theme.colors.success[500] as string,
    waste: theme.colors.red[600] as string,
    current: theme.colors.interactive.buttons.default_background as string,
    track: theme.colors.borderCard as string,
  };

  const { lo, hi, done } = state;
  const currentIndex = getMidpoint(state);
  const currentClip = searchClips[currentIndex];
  const currentPlayerClip = useMemo(() => (currentClip ? clipItemToClip(currentClip) : null), [currentClip]);

  return (
    <View flex={1} style={{ overflow: "hidden", fontFamily: "Inter, sans-serif" } as never}>
      {done ?
        <DumpingResult
          clips={searchClips}
          lo={lo}
          screenHeight={screenHeight}
          screenWidth={screenWidth}
          canUndo={canUndo}
          onUndo={handleUndo}
          onDone={onExit}
          tenantId={tenantId}
          siteId={siteId}
          cameraKey={cameraKey}
        />
      : <View flex={1} style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
          {/* Step indicator */}
          <View p="3xl" flexDirection="row" justifyContent="space-between" alignItems="center">
            <Text textStyle="sm/semibold" color="text">
              Dumping search
            </Text>
            <Text textStyle="xs/regular" color="textMuted">
              {currentClip ? formatTimestamp(currentClip.startTime) : ""} · clip {currentIndex + 1} of{" "}
              {searchClips.length}
            </Text>
          </View>

          {/* Current clip — first frame paused so a rapid scan holds still; the
              reviewer can hit play to inspect motion. Keyed on clip id to
              remount the player (and reset to the first frame) per step. */}
          <View
            flex={1}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 0 }}
            px="3xl"
            onLayout={(e) => setPlayerAreaHeight(e.nativeEvent.layout.height)}
          >
            {currentPlayerClip ?
              <View style={{ width: "100%" }}>
                <ClipVideoPlayer
                  key={currentPlayerClip.id}
                  clip={currentPlayerClip}
                  autoPlay={false}
                  screenHeight={screenHeight}
                  screenWidth={screenWidth}
                  selectedHour={0}
                  // Fill the measured area (minus breathing room) so the paused
                  // frame is as large as possible while staying fully visible.
                  maxHeightOverride={playerAreaHeight > 0 ? playerAreaHeight - 16 : undefined}
                />
              </View>
            : <Text textStyle="md/regular" color="textMuted">
                No clip to show here.
              </Text>
            }
          </View>

          {/* Timeline bar */}
          <View px="3xl" pt="xl">
            <Timeline total={searchClips.length} lo={lo} hi={hi} current={currentIndex} colors={timelineColors} />
          </View>

          {/* Verdict buttons */}
          <View flexDirection="row" gap="xl" p="3xl" justifyContent="center" alignItems="center">
            <Button label="No waste (←)" variantColor="success" onPress={handleNoWaste} />
            <Button label="Can't tell (S)" variant="secondary" variantColor="grey" onPress={handleSkip} />
            <Button label="Waste present (→)" variantColor="danger" onPress={handleWastePresent} />
            <Button label="Undo (⌫)" variant="link" variantColor="grey" onPress={handleUndo} disabled={!canUndo} />
          </View>
        </View>
      }
    </View>
  );
}

interface TimelineColors {
  safe: string;
  waste: string;
  current: string;
  track: string;
}

function Timeline({
  total,
  lo,
  hi,
  current,
  colors,
}: {
  total: number;
  lo: number;
  hi: number;
  current: number;
  colors: TimelineColors;
}) {
  const pct = (i: number) => (total <= 1 ? 0 : (i / (total - 1)) * 100);
  return (
    <div style={{ position: "relative", height: 8, borderRadius: 4, background: colors.track, width: "100%" }}>
      {/* Cleared-safe region: start → lo */}
      <div
        style={{
          position: "absolute",
          left: 0,
          width: `${pct(lo).toFixed(2)}%`,
          height: "100%",
          borderRadius: "4px 0 0 4px",
          background: colors.safe,
        }}
      />
      {/* Confirmed-waste region: hi → end */}
      <div
        style={{
          position: "absolute",
          left: `${pct(hi).toFixed(2)}%`,
          right: 0,
          height: "100%",
          borderRadius: "0 4px 4px 0",
          background: colors.waste,
        }}
      />
      {/* Current position marker */}
      <div
        style={{
          position: "absolute",
          left: `${pct(current).toFixed(2)}%`,
          top: "50%",
          width: 14,
          height: 14,
          borderRadius: "50%",
          background: colors.current,
          border: "2px solid #fff",
          transform: "translate(-50%, -50%)",
        }}
      />
    </div>
  );
}

function DumpingResult({
  clips,
  lo,
  screenHeight,
  screenWidth,
  canUndo,
  onUndo,
  onDone,
  tenantId,
  siteId,
  cameraKey,
}: {
  clips: ClipItem[];
  lo: number;
  screenHeight: number;
  screenWidth: number;
  canUndo: boolean;
  onUndo: () => void;
  onDone: () => void;
  tenantId: string;
  siteId: string;
  cameraKey: string;
}) {
  const router = useRouter();
  const [modalVisible, setModalVisible] = useState(false);
  const confirmDump = useConfirmDump({ siteId, cameraKey });
  // Background refresh of the dump's week WasteSense report on a successful
  // confirm — see the fire-and-forget call in handleConfirm below.
  const { regenerate: regenerateWasteSense } = useRegenerateWasteSenseForWeek();

  // The bisect's `lo` is the last clip whose (start-of-clip) thumbnail was still
  // clear, and the next clip's thumbnail already shows waste. Because thumbnails
  // are sampled near the start of each clip, the act of dumping happens *within*
  // `clips[lo]` — that's the evidence clip, not the following one. Hero on `lo`
  // and let the user step back and forth to hunt for a cleaner frame.
  const [index, setIndex] = useState(lo);
  const clip = clips[index];
  const playerClip = useMemo(() => (clip ? clipItemToClip(clip) : null), [clip]);

  // Measured height of the flex area between the header and the controls. Passed
  // to ClipVideoPlayer as its max-height so the clip grows to fill the available
  // space (still 16:9, centered) instead of the default 40%-of-window cap.
  const [playerAreaHeight, setPlayerAreaHeight] = useState(0);

  const canPrev = index > 0;
  const canNext = index < clips.length - 1;
  const goPrev = useCallback(() => setIndex((i) => (i > 0 ? i - 1 : i)), []);
  const goNext = useCallback(() => setIndex((i) => (i < clips.length - 1 ? i + 1 : i)), [clips.length]);

  const openModal = useCallback(() => setModalVisible(true), []);
  const closeModal = useCallback(() => setModalVisible(false), []);

  // The clip's start time is its identity (see clipItemToClip); it's what the
  // API stores as the dump `timestamp`.
  const handleConfirm = useCallback(
    async (value: ConfirmDumpFormValue) => {
      if (!clip) return;
      // Only the write itself decides success/failure for the modal. Once
      // mutateAsync resolves the dump is durably recorded, so this promise must
      // resolve too — otherwise a throw in the post-save side-effects below
      // would make the modal report "couldn't record the dump" and a retry
      // would write a *duplicate* row for the same clip.
      await confirmDump.mutateAsync({
        timestamp: clip.startTime,
        objectType: value.objectType,
        // description is now mandatory on the form value, so pass it directly.
        description: value.description,
      });

      // Post-save side-effects (toast + leaving the search). Deliberately kept
      // outside the awaited result: navigation/toast failures must not turn a
      // persisted dump into a modal error. Typed expo-router navigation
      // (pathname + params) mirrors how Scene Compare enters this flow.
      try {
        // Fire-and-forget: ask the backend to regenerate the WasteSense report
        // for the week this dump falls in, so the report reflects the new dump
        // without waiting for the next daily cron. Intentionally NOT awaited and
        // its own hook swallows all errors (including a 404 for a week with no
        // report yet) — a report-regen hiccup must never turn a persisted dump
        // into a modal error (same rationale as the awaited-write comment above).
        void regenerateWasteSense({ timestamp: clip.startTime });

        showCustomToast({ type: "success", message: "Dump recorded" });
        router.replace({
          pathname: "/tenants/[tenantId]/sites/[siteId]/cameras/[cameraKey]",
          params: { tenantId, siteId, cameraKey },
        });
      } catch {
        // Swallow: the dump is already saved, so the operation the modal
        // represents succeeded. Worst case the operator stays on the result and
        // navigates manually.
      }
    },
    [clip, confirmDump, regenerateWasteSense, router, tenantId, siteId, cameraKey],
  );

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // While the confirm modal is open it owns the keyboard (typing a
      // description); don't step clips underneath it.
      if (modalVisible) return;
      if (e.code === "ArrowLeft") goPrev();
      else if (e.code === "ArrowRight") goNext();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [goPrev, goNext, modalVisible]);

  return (
    <View flex={1} style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
      <View p="3xl" flexDirection="row" justifyContent="space-between" alignItems="center">
        <Text textStyle="sm/semibold" color="text">
          Dumping event narrowed down
        </Text>
        <Text textStyle="xs/regular" color="textMuted">
          {clip ? formatTimestamp(clip.startTime) : ""}
          {index === lo ? " · likely dumping clip" : ""}
        </Text>
      </View>

      {/* The full-resolution clip autoplays so the reviewer sees the dumping in
          motion. Keyed on clip id to reload cleanly when stepping. */}
      <View
        flex={1}
        style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 0 }}
        px="3xl"
        onLayout={(e) => setPlayerAreaHeight(e.nativeEvent.layout.height)}
      >
        {playerClip ?
          <View style={{ width: "100%" }}>
            <ClipVideoPlayer
              key={playerClip.id}
              clip={playerClip}
              screenHeight={screenHeight}
              screenWidth={screenWidth}
              selectedHour={0}
              // Fill the measured area (minus a little breathing room) so the
              // clip is as large as possible while staying fully visible.
              maxHeightOverride={playerAreaHeight > 0 ? playerAreaHeight - 16 : undefined}
            />
          </View>
        : <Text textStyle="md/regular" color="textMuted">
            No playable clip here.
          </Text>
        }
      </View>

      {/* Step back and forth from the boundary to find a clearer frame of the
          dumping. The current clip's offset from the detected boundary (`lo`)
          orients the user without re-running the search. */}
      <View flexDirection="row" gap="xl" px="3xl" pt="xl" alignItems="center" justifyContent="center">
        <Button label="← Previous" variant="secondary" variantColor="grey" onPress={goPrev} disabled={!canPrev} />
        <Text textStyle="xs/regular" color="textMuted">
          clip {index + 1} of {clips.length}
        </Text>
        <Button label="Next →" variant="secondary" variantColor="grey" onPress={goNext} disabled={!canNext} />
      </View>

      <View flexDirection="row" gap="xl" p="3xl" justifyContent="center" alignItems="center">
        <Button label="Undo last step (⌫)" variant="link" variantColor="grey" onPress={onUndo} disabled={!canUndo} />
        <Button label="Exit" variant="secondary" variantColor="grey" onPress={onDone} />
        <Button label="Confirm Dump" variantColor="danger" onPress={openModal} disabled={!clip} />
      </View>

      <ConfirmDumpModal
        visible={modalVisible}
        onClose={closeModal}
        onConfirm={handleConfirm}
        isSubmitting={confirmDump.isPending}
      />
    </View>
  );
}
